// src/components/layout/Navbar.js - top bar (leader and admin area).
// Audit FL-35: the group name is visible on phones too (truncated), a failed lookup
// shows "Můj účet" instead of an endless "Načítání…", the shared API client is used,
// the logout button is labelled, and running uploads are shown (FL-10).
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { ConfirmDialog } from '../ui';
import ChangePasswordDialog from '../ChangePasswordDialog';
import { UploadIndicator, useActiveUploads } from '../leader/UploadManager';

const Navbar = ({ children }) => {
  const { user, logout } = useAuth();
  const uploads = useActiveUploads();
  const [groupName, setGroupName] = useState(null);
  const [groupFailed, setGroupFailed] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const impersonating = Boolean(user?.impersonatedBy);
  const isLeader = user?.role === 'leader';

  useEffect(() => {
    if (!isLeader) return undefined;
    let cancelled = false;
    setGroupFailed(false);
    api
      .get('/dancers/group')
      .then((res) => {
        if (!cancelled) setGroupName((res.data && res.data.name) || '');
      })
      .catch(() => {
        if (!cancelled) setGroupFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isLeader, user?.id]);

  const handleLogout = () => {
    if (uploads.length > 0) setConfirmLogout(true);
    else logout();
  };
  const closeConfirm = useCallback(() => setConfirmLogout(false), []);

  let accountLabel = 'Administrátor';
  if (isLeader) accountLabel = groupName || (groupFailed || groupName === '' ? 'Můj účet' : 'Načítání…');

  return (
    <nav className="bg-white shadow" aria-label="Horní lišta">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-2 px-3 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-center gap-1 sm:gap-2">
          {children}
          <Link
            to={user?.role === 'admin' ? '/admin' : '/'}
            className="whitespace-nowrap rounded-md text-lg font-bold text-indigo-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:text-xl"
          >
            Best of the Best
          </Link>
        </div>

        {user && (
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <UploadIndicator />
            <button
              type="button"
              onClick={() => setChangePassword(true)}
              disabled={impersonating}
              title={impersonating ? accountLabel : `${accountLabel} – změnit heslo`}
              aria-label={`Můj účet ${accountLabel}: změnit heslo`}
              className="flex min-w-0 items-center gap-1.5 rounded-md px-1 py-1 text-sm text-gray-700 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-default disabled:hover:bg-transparent"
            >
              <User className="hidden h-5 w-5 flex-shrink-0 text-gray-400 sm:block" aria-hidden="true" />
              <span className="max-w-[7rem] truncate sm:max-w-[16rem]">{accountLabel}</span>
            </button>
            <button
              type="button"
              onClick={handleLogout}
              aria-label="Odhlásit se"
              className="inline-flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 sm:px-3"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span className="hidden md:inline">Odhlásit se</span>
            </button>
          </div>
        )}
      </div>

      {changePassword && <ChangePasswordDialog onClose={() => setChangePassword(false)} />}

      {confirmLogout && (
        <ConfirmDialog
          title="Probíhá nahrávání"
          message="Právě se nahrává soubor. Pokud se teď odhlásíte, nahrávání se nemusí dokončit. Opravdu se chcete odhlásit?"
          confirmLabel="Odhlásit se"
          cancelLabel="Zůstat přihlášen"
          danger
          onConfirm={() => {
            setConfirmLogout(false);
            logout();
          }}
          onCancel={closeConfirm}
        />
      )}
    </nav>
  );
};

export default Navbar;
